import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListFormPageHeader from '../../components/ListFormPageHeader';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import Swal from 'sweetalert2';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import {
    deleteConsumption,
    getConsumptionList,
} from '../../services/ConsumptionNoteServices';
import { buildConsumptionNoteQueryParams } from '../../utils/listViewDateRanges';
import { getDefaultListViewConfig } from '../../config/listViewSettingsConfig';
import { bootstrapListViewLoad } from '../../utils/listViewBootstrap';
import ListPageGradientSummary from '../../components/ListPageGradientSummary';
import { buildGradientCard, formatRecordStatus, splitRecordsByStatus, buildCancelledSummaryCard, formatQty } from '../../utils/listSummaryUtils';
import moment from 'moment';

function ConsumptionNoteTable() {
    const [data, setData] = useState({ records: [] });
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);

    const todayDateLabel = moment().format('DD-MM-YYYY');
    const { active: activeRecords, cancelled: cancelledRecords } = useMemo(
        () => splitRecordsByStatus(data.records),
        [data.records],
    );

    const highlightCard = useMemo(() => {
        const todayCount = activeRecords.filter((row) => row.consumption_date === todayDateLabel).length;
        return buildGradientCard('Today Consumption', todayCount, 'today');
    }, [activeRecords, todayDateLabel]);

    const summaryCards = useMemo(() => {
        const cards = [
            buildGradientCard('Total Notes', activeRecords.length, 'blue'),
            buildGradientCard('Active', activeRecords.length, 'teal'),
        ];
        const cancelledCard = buildCancelledSummaryCard(cancelledRecords, {
            entityLabel: 'note cancelled',
        });
        if (cancelledCard) cards.push(cancelledCard);
        return cards;
    }, [activeRecords, cancelledRecords]);

    const cancelledPopup = useMemo(() => ({
        title: `Cancelled Consumption Notes (${cancelledRecords.length})`,
        emptyMessage: 'No cancelled consumption notes in the current list.',
        columns: [
            { key: 'consumption_no_display', label: 'Consumption No', align: 'center' },
            { key: 'consumption_date', label: 'Date', align: 'center' },
            { key: 'plant_alias', label: 'Plant', align: 'left' },
            { key: 'consumption_mode', label: 'Basis', align: 'left' },
            { key: 'total_concrete_qty', label: 'Concrete', align: 'right' },
        ],
        getRows: () => cancelledRecords,
        mapRow: (row) => ({
            consumption_no_display: row.consumption_no_display,
            consumption_date: row.consumption_date,
            plant_alias: row.plant_alias,
            consumption_mode: row.consumption_mode,
            total_concrete_qty: formatQty(row.total_concrete_qty),
        }),
        footerRowBuilder: (rows) => ({
            consumption_no_display: 'Total',
            consumption_date: '',
            plant_alias: '',
            consumption_mode: `${rows.length} Note`,
            total_concrete_qty: formatQty(rows.reduce((s, r) => s + (Number(r.total_concrete_qty) || 0), 0)),
        }),
    }), [cancelledRecords]);

    const columns = useMemo(() => [
        { fieldName: 'plant_alias', headerName: 'Plant' },
        { fieldName: 'consumption_no_display', headerName: 'Consumption No' },
        { fieldName: 'consumption_date', headerName: 'Date' },
        { fieldName: 'consumption_mode', headerName: 'Basis' },
        { fieldName: 'production_mode', headerName: 'Production' },
        { fieldName: 'total_concrete_qty', headerName: 'Concrete (cum)' },
        { fieldName: 'material_summary', headerName: 'Materials' },
        { fieldName: 'status', headerName: 'Status' },
        { fieldName: 'id', headerName: 'ROW_ACTIONS' },
    ], []);

    const loadConsumptionList = useCallback((config) => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return Promise.resolve();
        }
        const activeConfig = config || getDefaultListViewConfig('consumption_note');
        setIsLoading(true);
        return getConsumptionList(cookies, {
            ...buildConsumptionNoteQueryParams(activeConfig),
            limit: 500,
        })
            .then((response) => {
                const records = (response.consumption_list || []).map((row) => {
                    const isActive = row.status === true || row.status === 1 || row.status === 'Active';
                    return {
                        ...row,
                        consumption_no_display: row.consumption_no,
                        status: formatRecordStatus(isActive),
                        hide_row_edit: isActive ? 'No' : 'Yes',
                        hide_row_delete: isActive ? 'Yes' : 'No',
                    };
                });
                setData({ records });
            })
            .catch((error) => {
                displayError(error?.response?.data || error, 'Failed to load consumption notes');
                setData({ records: [] });
            })
            .finally(() => setIsLoading(false));
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        loadConsumptionList(config);
    }, [loadConsumptionList]);

    useEffect(() => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return undefined;
        }
        let cancelled = false;
        bootstrapListViewLoad({
            cookies,
            viewKey: 'consumption_note',
            loadFn: loadConsumptionList,
            isCancelled: () => cancelled,
        });
        return () => { cancelled = true; };
    }, [cookies, cookies.myToken, loadConsumptionList]);

    const handleOpen = (id) => {
        navigate(`/ConsumptionNoteEdit/${id}`);
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Delete consumption note?',
            icon: 'warning',
            showCancelButton: true,
        }).then((result) => {
            if (!result.isConfirmed) return;
            deleteConsumption(cookies, id)
                .then(() => {
                    Swal.fire('Deleted', 'Consumption note deleted.', 'success');
                    loadConsumptionList();
                })
                .catch((error) => displayError(error?.response?.data || error, 'Delete Failed'));
        });
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListFormPageHeader
                    module="STORE"
                    title="List of Consumption Notes"
                    viewKey="consumption_note"
                    onSettingsSaved={handleSettingsSaved}
                    formLink="/ConsumptionNote"
                    addNewLabel="New Consumption Note"
                />
                <ListPageGradientSummary
                    highlightCard={highlightCard}
                    cards={summaryCards}
                    cancelledPopup={cancelledPopup}
                />
                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title="List of Consumption Notes"
                            data={data.records}
                            columns={columns}
                            isLoading={isLoading}
                            onRowEdit={handleOpen}
                            onRowDelete={handleDelete}
                            hideDelete={false}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ConsumptionNoteTable;
